import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { map } from 'rxjs/operators';
import { JsonConvert } from 'json2typescript';
import { HrConfigurationService } from 'humanresources-common-shared';

import { OrganizationCopyModel } from '../model/organization-copy-model';
import { PerOrganizationGeneralInfoModel } from '../organization-picker/model/per-organization-general-info-model';
import { PerOrganizationGeneralInfoQueryModel } from '../organization-picker/model/per-organization-general-info-query-model';
import { PerObjectDefinitionModel } from '../object-management/object-definition/model/per-object-definition-model';
import { OrganizationDefinitionQueryModel } from '../organization-picker/model/organization-definition-query-model';
import { OrganizationDefinitionModel } from '../organization-picker/model/organization-definition-model';
import { kovanQueryPageSerializer } from 'ui-shared';
import {ObjectCardDetailFieldModel} from "../object-management/object-definition/model/object-card-detail-field-model";
import {
    OrganizationIsChildControlModel
} from '../model/organization-is-child-control-model';

@Injectable({
    providedIn: 'root',
})
export class OrganizationDefinitionService {
    endpoint: string;
    readonly serviceRootPattern = '/organizationGeneralInfoService/organizationGeneralInfo/';

    constructor(
        private httpClient: HttpClient,
        private configService: HrConfigurationService,
    ) {
        this.endpoint = configService.config.hr.personnel;
    }

    public deleteById(id: number): Observable<any> {
        const pattern = '/organizationDefinitionService/organizationDefinition/' + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public existsByUuid(uuid: string): Observable<Boolean> {
        const url = `${this.endpoint}${this.serviceRootPattern}exists/uuid/${uuid}`;
        return this.httpClient.get<HvlResponse<Boolean>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, Boolean);
            }),
        );
    }

    public queryOrganizationPage(
        pagination: OrganizationDefinitionQueryModel,
    ): Observable<HvlPage<Array<OrganizationDefinitionModel>>> {
        return this.queryPageInfo(pagination);
    }

    public queryPage(
        pagination: OrganizationDefinitionQueryModel,
    ): Observable<HvlPage<Array<OrganizationDefinitionModel>>> {
        const pattern = this.serviceRootPattern + 'page';
        return this.httpClient
            .post<HvlResponse<HvlPage<OrganizationDefinitionModel[]>>>(this.endpoint + pattern, pagination)
            .pipe(
                map((hvlpage) => {
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<OrganizationDefinitionModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }

                    const converter = new JsonConvert();
                    const model = converter.deserializeArray(hvlpage.body.data, OrganizationDefinitionModel);
                    return new HvlPage<Array<OrganizationDefinitionModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public queryPageInfo(
        pagination: OrganizationDefinitionQueryModel,
    ): Observable<HvlPage<Array<OrganizationDefinitionModel>>> {
        const pattern = '/organizationDefinitionService/organizationDefinition/' + 'page';
        const converter = new JsonConvert();

        return this.httpClient
            .post<HvlResponse<HvlPage<OrganizationDefinitionModel[]>>>(
                this.endpoint + pattern,
                kovanQueryPageSerializer(pagination, OrganizationDefinitionQueryModel),
            )
            .pipe(
                map((hvlpage) => {
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<OrganizationDefinitionModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }

                    const model = converter.deserializeArray(hvlpage.body.data, OrganizationDefinitionModel);
                    return new HvlPage<Array<OrganizationDefinitionModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public existsById(id: number): Observable<Boolean> {
        const url = `${this.endpoint}${this.serviceRootPattern}exists/${id}`;
        return this.httpClient.get<HvlResponse<Boolean>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, Boolean);
            }),
        );
    }

    public getByUuid(uuid: string): Observable<PerOrganizationGeneralInfoModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}uuid/${uuid}`;
        return this.httpClient.get<HvlResponse<PerOrganizationGeneralInfoModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerOrganizationGeneralInfoModel);
            }),
        );
    }

    public update(model: PerOrganizationGeneralInfoModel): Observable<PerOrganizationGeneralInfoModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerOrganizationGeneralInfoModel>>(
                this.endpoint + pattern,
                converter.serialize(model, PerOrganizationGeneralInfoModel),
            )
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeObject(data.body, PerOrganizationGeneralInfoModel);
                }),
            );
    }

    public exists(queryModel: PerOrganizationGeneralInfoQueryModel): Observable<Boolean> {
        const pattern = this.serviceRootPattern + 'exists';
        return this.httpClient.post<HvlResponse<Boolean>>(this.endpoint + pattern, queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, Boolean);
            }),
        );
    }

    public getById(id: number): Observable<PerOrganizationGeneralInfoModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}${id}`;
        return this.httpClient.get<HvlResponse<PerOrganizationGeneralInfoModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerOrganizationGeneralInfoModel);
            }),
        );
    }

    public getParentById(id: number): Observable<PerOrganizationGeneralInfoModel> {
        const url = this.endpoint + this.serviceRootPattern + 'getParent/' + id;
        return this.httpClient.get<HvlResponse<PerOrganizationGeneralInfoModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerOrganizationGeneralInfoModel);
            }),
        );
    }

    public create(model: PerOrganizationGeneralInfoModel): Observable<number> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();

        return this.httpClient
            .post<HvlResponse<number>>(
                this.endpoint + pattern,
                converter.serialize(model, PerOrganizationGeneralInfoModel),
            )
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }

    public saveOrganization(perObjectDefinitionModel: PerObjectDefinitionModel): Observable<PerObjectDefinitionModel> {
        const pattern = this.serviceRootPattern + 'saveOrganization';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<PerObjectDefinitionModel>>(
                this.endpoint + pattern,
                converter.serialize(perObjectDefinitionModel, PerObjectDefinitionModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerObjectDefinitionModel);
                }),
            );
    }

    public createOrganization(model: OrganizationDefinitionModel): Observable<PerObjectDefinitionModel> {
        const pattern = '/organizationDefinitionService/organizationDefinition/' + 'save';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<OrganizationDefinitionModel>>(
                this.endpoint + pattern,
                converter.serialize(model, OrganizationDefinitionModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerObjectDefinitionModel);
                }),
            );
    }

    // public copyOrganization(model: OrganizationCopyModel): Observable<number> {
    //     const pattern = this.serviceRootPattern +'copy';
    //     return this.httpClient.post<HvlResponse<number>>(this.endpoint + pattern, model).pipe(
    //         map((data) => {
    //             return data.body;
    //         }),
    //     );
    // }
    public copyOrganization(model: OrganizationCopyModel): Observable<any> {
        const pattern = '/organizationDefinitionService/organizationDefinition/' + 'copy';
        const converter = new JsonConvert();
        return this.httpClient.post<HvlResponse<any>>(this.endpoint + pattern, converter.serialize(model, null)).pipe(
            map((data) => {
                return data.body || null;
            }),
        );
    }


    public queryList(
        queryModel: PerOrganizationGeneralInfoQueryModel,
    ): Observable<Array<PerOrganizationGeneralInfoModel>> {
        const pattern = this.serviceRootPattern + 'query';
        return this.httpClient
            .post<HvlResponse<Array<PerOrganizationGeneralInfoModel>>>(this.endpoint + pattern, queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerOrganizationGeneralInfoModel);
                }),
            );
    }

    public queryListWithLocale(
        queryModel: PerOrganizationGeneralInfoQueryModel,
        locale: string,
    ): Observable<Array<PerOrganizationGeneralInfoModel>> {
        const pattern = this.serviceRootPattern + 'query/byLocale/' + locale;
        return this.httpClient
            .post<HvlResponse<Array<PerOrganizationGeneralInfoModel>>>(this.endpoint + pattern, queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerOrganizationGeneralInfoModel);
                }),
            );
    }
    public objectCodeNumeratorGenerator(): Observable<String> {
        const url =
            '/organizationDefinitionMovementService/organizationDefinitionMovement/' + 'objectCodeNumeratorGenerator';
        return this.httpClient.get<HvlResponse<String>>(this.endpoint + url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, String);
            }),
        );
    }
    public getOrganizationCardDetails(id: number): Observable<ObjectCardDetailFieldModel> {
        const pattern = '/organizationDefinitionService/organizationDefinition/getOrganizationCardDetails/' + id;
        const serviceUrl = this.endpoint + pattern; // Full path
        return this.httpClient.get<HvlResponse<ObjectCardDetailFieldModel>>(serviceUrl).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, ObjectCardDetailFieldModel);
            }),
        );
    }

    public isChildOfOrganizations(
        model: OrganizationIsChildControlModel,
    ): Observable<Boolean> {

        const pattern = '/organizationDefinitionService/organizationDefinition/' + 'isChildOfOrganizations';
        return this.httpClient
            .post<HvlResponse<Boolean>>(this.endpoint + pattern, model)
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }
}
